import type { SearchRequest } from './api';
import type { RefObject } from 'react';

export type SearchDraft = {
  subject: string; body: string; from: string; to: string; cc: string;
  receivedFrom: string; receivedTo: string; attachmentName: string; category: string;
  sizeMin: string; sizeMax: string;
  hasAttachments: '' | 'yes' | 'no'; unread: '' | 'yes' | 'no'; flagged: '' | 'yes' | 'no';
  importance: '' | 'low' | 'normal' | 'high';
};
export type SearchDraftErrors = Partial<Record<'sizeMin' | 'sizeMax', 'webmail.searchAdvanced.sizeInvalid' | 'webmail.searchAdvanced.sizeRangeInvalid'>>;

/** Search people/category arrays contain text terms, not validated SMTP principals. */
export const searchDraftList = (value: string): string[] => value.split(',').map(part => part.trim()).filter(Boolean);

/** Explicit directory selection appends its address without rewriting existing substring terms. */
export const appendSearchPerson = (value: string, email: string): string => value ? `${value}, ${email}` : email;

export const draftFromFilter = (filter: SearchRequest['filter']): SearchDraft => ({
  subject: filter?.subject ?? '', body: filter?.body ?? '', from: filter?.from?.join(', ') ?? '', to: filter?.to?.join(', ') ?? '', cc: filter?.cc?.join(', ') ?? '',
  receivedFrom: filter?.received?.from ?? '', receivedTo: filter?.received?.to ?? '', attachmentName: filter?.attachmentName ?? '', category: filter?.categories?.join(', ') ?? '',
  sizeMin: filter?.size?.min === undefined ? '' : String(filter.size.min), sizeMax: filter?.size?.max === undefined ? '' : String(filter.size.max),
  hasAttachments: filter?.hasAttachments === undefined ? '' : filter.hasAttachments ? 'yes' : 'no',
  unread: filter?.unread === undefined ? '' : filter.unread ? 'yes' : 'no',
  flagged: filter?.flagged === undefined ? '' : filter.flagged ? 'yes' : 'no', importance: filter?.importance ?? '',
});

/** Byte counts remain distinct from omitted bounds; dates stay unchanged UTC-day strings. */
export function validateSearchDraft(draft: SearchDraft): SearchDraftErrors {
  const errors: SearchDraftErrors = {};
  for (const key of ['sizeMin', 'sizeMax'] as const) {
    if (draft[key] !== '' && (!/^\d+$/.test(draft[key]) || !Number.isSafeInteger(Number(draft[key])))) errors[key] = 'webmail.searchAdvanced.sizeInvalid';
  }
  if (!Object.keys(errors).length && draft.sizeMin !== '' && draft.sizeMax !== '' && Number(draft.sizeMin) > Number(draft.sizeMax)) {
    errors.sizeMin = errors.sizeMax = 'webmail.searchAdvanced.sizeRangeInvalid';
  }
  return errors;
}

export function filterFromDraft(draft: SearchDraft): NonNullable<SearchRequest['filter']> {
  if (Object.keys(validateSearchDraft(draft)).length) throw new RangeError('Invalid search byte bounds');
  const list = searchDraftList;
  return {
    ...(draft.subject.trim() && { subject: draft.subject.trim() }),
    ...(draft.body.trim() && { body: draft.body.trim() }),
    ...(list(draft.from).length && { from: list(draft.from) }),
    ...(list(draft.to).length && { to: list(draft.to) }),
    ...(list(draft.cc).length && { cc: list(draft.cc) }),
    ...((draft.receivedFrom || draft.receivedTo) && { received: { ...(draft.receivedFrom && { from: draft.receivedFrom }), ...(draft.receivedTo && { to: draft.receivedTo }) } }),
    ...(draft.attachmentName.trim() && { attachmentName: draft.attachmentName.trim() }),
    ...(list(draft.category).length && { categories: list(draft.category) }),
    ...((draft.sizeMin !== '' || draft.sizeMax !== '') && { size: { ...(draft.sizeMin !== '' && { min: Number(draft.sizeMin) }), ...(draft.sizeMax !== '' && { max: Number(draft.sizeMax) }) } }),
    ...(draft.hasAttachments && { hasAttachments: draft.hasAttachments === 'yes' }),
    ...(draft.unread && { unread: draft.unread === 'yes' }),
    ...(draft.flagged && { flagged: draft.flagged === 'yes' }),
    ...(draft.importance && { importance: draft.importance }),
  };
}

export interface SearchFilterDialogProps {
  fallbackFocusRef?: RefObject<HTMLElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: SearchDraft;
  onDraftChange: <K extends keyof SearchDraft>(key: K, value: SearchDraft[K]) => void;
  errors: SearchDraftErrors;
  /** False means local validation failed; reveal Properties before focusing errors. */
  onApply: () => boolean;
  onClear: () => void;
}
